import React, { useState, useEffect, createContext } from 'react';
import mapboxgl from 'mapbox-gl';

import { Layer, MapboxLayerState, PlatMapSettings, PlatmapLayerContext } from '@mono/types/src/platmap';
import { useSettingsContext } from '@mono/platmap-settings-context';
import { diff } from '@mono/diff';
import { getStyle, getStyles, Style, ListedStyle } from '@mono/mapbox-service-api';
import { isMapboxLayer } from 'layerTypeUtils';
import { usePlatStageContext } from '@mono/use-plat-stage';

export const MapboxServiceContext = createContext<
    PlatmapLayerContext & {
        styles: ListedStyle[] | null;
        layersForStyle: {
            styleId: string;
            layers: Style[];
        }[];
    }
>({
    styles: [],
    layersForStyle: [],
    setMap: () => {},
    shuffle: () => {},
});

const getStyleIds = (settings: PlatMapSettings) => {
    return settings.layers.filter(isMapboxLayer).map((l) => l.state.mapboxStyleId);
};

export const MapboxServiceContextProvider = ({
    children,
    mapboxAccessTokenSk,
    delimiter,
    anchorId,
}: {
    children: JSX.Element;
    mapboxAccessTokenSk: string;
    delimiter: string;
    anchorId: string;
}) => {
    const getMapboxStyleIndex = (listedStyle: ListedStyle) => {
        return ['plat', 'mapbox', listedStyle.id].join(delimiter);
    };
    const { settings } = useSettingsContext();
    const { stage } = usePlatStageContext();

    const [state, setState] = useState<{
        styles: ListedStyle[] | null;
        layersForStyle: {
            styleId: string;
            layers: any[];
        }[];
        map: mapboxgl.Map | null;
        shuffleIndex: string;
    }>({
        styles: null,
        layersForStyle: [],
        map: null,
        shuffleIndex: "Everyday I'm shuffling",
    });

    const downloadStyle = async (styleId: string) => {
        const layer = state.styles?.find((l) => l.id == styleId);
        if (!layer) return;
        if (state.layersForStyle.find((l) => l.styleId === styleId)) return;

        const style = await getStyle(styleId);
        setState((state) => ({
            ...state,
            layersForStyle: [
                ...state.layersForStyle,
                {
                    styleId,
                    layers: style.layers.reverse(),
                },
            ],
        }));
    };

    useEffect(() => {
        getStyleIds(settings).forEach(downloadStyle);
    }, [getStyleIds(settings), state.styles]);

    useEffect(() => {
        const fetchStyles = async () => {
            const styles = await getStyles();
            setState((state) => ({
                ...state,
                styles,
            }));
        };
        fetchStyles();
    }, []);

    useEffect(() => {
        if (!state.map || !state.styles) {
            // Loading...
            return;
        }

        const mapboxLayerItems = settings.layers.filter(isMapboxLayer);

        const sourcesToRender = mapboxLayerItems
            .filter((l) => {
                if (!l.state.mapboxStyleId) return false;
                return true;
            })
            .map((layer) => {
                const { mapboxStyleId } = layer.state;
                if (!state.styles) return null;
                const style = state.styles.find((s) => s.id === mapboxStyleId);
                if (!style) return null;

                return getMapboxStyleIndex(style);
            })
            .filter((s): s is string => s !== null);

        for (const sourceIndex of sourcesToRender) {
            const [, , styleId] = sourceIndex.split(delimiter);
            const style = state.styles.find((s) => s.id === styleId);
            if (!style) continue;

            for (const sourceId of Object.keys(style.sources)) {
                const mapboxSourceId = ['plat', 'mapbox', styleId, sourceId].join(delimiter);
                if (state.map.getSource(mapboxSourceId)) continue;

                state.map.addSource(mapboxSourceId, style.sources[sourceId]);
            }
        }

        const mapboxLayers = state.map.getStyle().layers;
        if (!mapboxLayers) return;

        const existingLayers = mapboxLayers
            .filter((l) => {
                if (!l.id.startsWith(['plat', 'mapbox', ''].join(delimiter))) return false;
                if (l.id.includes(anchorId)) return false;
                return true;
            })
            .map((a) => a.id);

        const targetLayers = settings.layers
            .filter((l): l is Layer & { state: MapboxLayerState } => {
                if (l.state.type !== 'mapbox') return false;
                if (!l.state.mapboxStyleId) return false;
                return true;
            })
            .map((layer) => {
                const { mapboxStyleId } = layer.state;
                if (!state.styles) return [];
                const style = state.layersForStyle.find((s) => s.styleId === mapboxStyleId);
                if (!style) return [];

                return style.layers
                    .filter((l) => {
                        return !layer.state.disabledLayers.includes(l.id);
                    })
                    .map((styleLayer) => {
                        return ['plat', 'mapbox', layer.id, style.styleId, styleLayer.id].join(delimiter);
                    });
            })
            .flat();

        const { add, remove } = diff(existingLayers, targetLayers);

        for (const layerIndex of remove) {
            state.map.removeLayer(layerIndex);
        }

        for (const layerIndex of add) {
            const [, , , styleId, styleLayerId] = layerIndex.split(delimiter);
            const style = state.styles.find((s) => s.id === styleId);
            const layer = state.layersForStyle.find((l) => l.styleId === styleId)?.layers.find((l) => l.id === styleLayerId);

            if (!style || !layer) continue;
            state.map.addLayer({
                ...(layer as any),
                source: ['plat', 'mapbox', styleId, layer.source].join(delimiter),
                id: layerIndex,
            });
        }
        const mapboxStyle = state.map.getStyle();

        for (const layer of mapboxLayerItems) {
            const layerAnchorId = ['plat', 'mapbox', layer.id, anchorId].join(delimiter);
            let previousLayer = layerAnchorId;

            if (!mapboxStyle.layers.map((l) => l.id).includes(layerAnchorId)) {
                setTimeout(() => {
                    setState((state) => {
                        return { ...state, shuffleIndex: state.shuffleIndex + Math.random().toString() };
                    });
                }, 16);
                continue;
            }

            const { mapboxStyleId, disabledLayers } = layer.state;
            const mapboxLayers = state.layersForStyle.find((s) => s.styleId === mapboxStyleId);
            if (!mapboxLayers) continue;

            const mapboxLayerIndices = mapboxLayers.layers
                .filter((l) => !disabledLayers.includes(l.id))
                .map((l) => ['plat', 'mapbox', layer.id, mapboxStyleId, l.id].join(delimiter));

            if (!mapboxLayerIndices) continue;

            for (const layerIndex of mapboxLayerIndices) {
                state.map.moveLayer(layerIndex, previousLayer);
                previousLayer = layerIndex;
            }
        }
    }, [state.map, state.layersForStyle, state.styles, state.shuffleIndex]);

    return (
        <MapboxServiceContext.Provider
            value={{
                styles: state.styles,
                layersForStyle: state.layersForStyle,
                shuffle: (shuffleIndex: string) => {
                    setState((state) => ({
                        ...state,
                        shuffleIndex,
                    }));
                },
                setMap: (map: mapboxgl.Map) => setState((state) => ({ ...state, map })),
            }}
        >
            {children}
        </MapboxServiceContext.Provider>
    );
};
